/**
 * lumaGrain — a photographic grain for the DISPLAY-referred end of the post
 * chain (lighting agent 2026-09-25, Atlas realism §10 R11).
 *
 * three's FilmNode multiplies the colour by (1 + intensity·(noise + 0.1)) —
 * a grain that BRIGHTENS the frame on average and sits in the HDR chain, where
 * the anti-aliasing smears it and the upscaler resamples it into crawl. Film
 * grain is none of that: it is zero-mean, it scales with exposure (dense
 * where the negative took light), and on a display it doubles as the dither
 * that breaks 8-bit banding in skies and fog.
 *
 *   g   = 2·rand(uv, t) − 1                         (zero-mean, per pixel, per frame)
 *   out = c · (1 + intensity · g) + (rand'(uv, t) − ½) / 255
 *
 * Intensity 0.01–0.02. `lumaGrainCpu` is the oracle.
 */
import { Fn, vec4, vec2, float, uv, fract, time, rand } from 'three/tsl';

/** CPU oracle for one channel: the value, the two uniform randoms in [0,1). */
export function lumaGrainCpu(c: number, intensity: number, r1: number, r2: number): number {
  const g = 2 * r1 - 1;
  return c * (1 + intensity * g) + (r2 - 0.5) / 255;
}

export function createLumaGrainNode(input: unknown, intensity: number): unknown {
  return Fn(() => {
    const c = vec4(input as never);
    const t = fract(time.mul(0.6180339887));
    const r1 = rand(fract(uv().add(vec2(t, t.mul(1.37)))));
    const r2 = rand(fract(uv().mul(1.713).add(vec2(t.mul(0.71), t))));
    const g = float(r1).mul(2).sub(1);
    const rgb = c.rgb.mul(g.mul(intensity).add(1)).add(float(r2).sub(0.5).div(255));
    return vec4(rgb, c.a);
  })();
}
